import { useMemo } from 'react';
import { UseQuerySubscription } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import WorkspaceAPI from '../WorkspaceAPI';
import { WorkspaceAPIResponse } from '../WorkspaceAPI.model';
import useMyWorkspacesChildren from './useMyWorkspacesChildren';
import useMultipleQueries from '../../../shared/util/rtk/useQueries';

type Options = Parameters<UseQuerySubscription<any>>[1];

function useMyWorkspacesChildrenAndWorkspaces(options?: Options) {
  const {
    data: childWorkspacesDict,
    isLoading: isLoadingChildren,
    isFetching: isFetchingChildren,
    error: childrenError,
  } = useMyWorkspacesChildren(options);

  const childWorkspaceIds = useMemo(
    () => Object.keys(childWorkspacesDict ?? {}),
    [childWorkspacesDict]
  );

  const childWorkspacesResponse = useMultipleQueries<
    WorkspaceAPIResponse,
    string
  >(WorkspaceAPI.endpoints.getWorkspaceById, childWorkspaceIds, {
    ...options,
    skip: !childWorkspaceIds.length || options?.skip,
  });

  const childWorkspacesById = useMemo<
    Record<string, WorkspaceAPIResponse>
  >(() => {
    const responseList =
      childWorkspacesResponse.currentData ?? childWorkspacesResponse.data ?? [];

    return responseList.reduce<Record<string, WorkspaceAPIResponse>>(
      (acc, workspace, index) => {
        const workspaceId = childWorkspaceIds[index];
        if (workspace && workspaceId) {
          acc[workspaceId] = workspace;
        }
        return acc;
      },
      {}
    );
  }, [
    childWorkspaceIds,
    childWorkspacesResponse.currentData,
    childWorkspacesResponse.data,
  ]);

  return {
    ...childWorkspacesResponse,
    childWorkspaceIds,
    childWorkspacesDict,
    childWorkspacesById,
    isLoadingChildren,
    isFetchingChildren,
    childrenError,
  };
}

export default useMyWorkspacesChildrenAndWorkspaces;
